import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarClockIcon, CheckIcon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import type { FollowUpLead } from '../data/followUps';
export type ActionMode = 'call' | 'email' | 'schedule';
type LeadActionModalProps = {
  lead: FollowUpLead | null;
  mode: ActionMode;
  onClose: () => void;
  onSubmit: (leadId: string, mode: ActionMode, payload: Record<string, string>) => void;
};
const callOutcomes = ['Spoke with borrower', 'Left voicemail', 'No answer', 'Wrong number'];

/** Format an ISO string into the local value a datetime-local input expects. */
function toDateTimeInputValue(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
export function LeadActionModal({
  lead,
  mode,
  onClose,
  onSubmit
}: LeadActionModalProps) {
  const [outcome, setOutcome] = useState(callOutcomes[0]);
  const [notes, setNotes] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  useEffect(() => {
    if (!lead) return;
    setOutcome(callOutcomes[0]);
    setNotes('');
    setSubject(`Following up on your ${lead.loanPurpose.toLowerCase()} loan`);
    setBody(`Hi ${lead.name.split(' ')[0]},\n\nI wanted to follow up regarding your ${lead.loanPurpose.toLowerCase()} loan. Do you have a few minutes this week to connect?\n\nBest regards,\nMilan R.\nUnited Mortgage Lending`);
    setFollowUpDate(toDateTimeInputValue(lead.nextFollowUp));
  }, [lead, mode]);
  useEffect(() => {
    if (!lead) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lead, onClose]);
  const isCall = mode === 'call';
  const isSchedule = mode === 'schedule';
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!lead) return;
    const payload: Record<string, string> = isCall ? {
      outcome,
      notes
    } : isSchedule ? {
      nextFollowUp: new Date(followUpDate).toISOString()
    } : {
      subject,
      body
    };
    onSubmit(lead.id, mode, payload);
  };
  const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20';
  return <AnimatePresence data-id="element-356" data-mp="1i1pcodhbs61r">
      {lead && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-357" data-mp="1m4vn7xbh0je1">
          <motion.button type="button" aria-label="Close" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="absolute inset-0 h-full w-full cursor-default bg-ink/40" data-id="element-358" data-mp="185w392ywkso7" />
          <motion.div role="dialog" aria-modal="true" aria-label={isCall ? 'Log call' : isSchedule ? 'Set next follow-up' : 'Send email'} initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 420,
        damping: 34
      }} className="relative w-full max-w-[520px] overflow-hidden rounded-xl bg-white shadow-2xl" data-id="element-359" data-mp="olne752i6gak">
            <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-4" data-id="element-360" data-mp="1m9fcy3h7lpk9">
              <span className={['flex h-9 w-9 items-center justify-center rounded-lg', isCall ? 'bg-brand-soft text-brand-dark' : isSchedule ? 'bg-success-soft text-success' : 'bg-navy-soft text-navy'].join(' ')} data-id="element-361" data-mp="1hauby1u4v1ur">
                {isCall ? <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-362" data-mp="3uf7ptx5j4nrk" /> : isSchedule ? <CalendarClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-363" data-mp="38n6z0pmom5m" /> : <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-364" data-mp="31qsdxjhp2lkg" />}
              </span>
              <div className="min-w-0" data-id="element-365" data-mp="36p3oxxjq7249">
                <h2 className="text-[16px] font-bold text-ink" data-id="element-366" data-mp="36q5ch1hxhh2v">
                  {isCall ? 'Log call' : isSchedule ? 'Set next follow-up' : 'Send email'}
                </h2>
                <p className="mt-0.5 truncate text-[12.5px] text-gray-500" data-id="element-367" data-mp="3fqjxs0uw6w6d">
                  {lead.name} · {isCall || isSchedule ? lead.phone : lead.email}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100" data-id="element-368" data-mp="uzb503w09bf2">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-369" data-mp="2bhbg8ea5e0z9" />
              </button>
            </header>

            <form onSubmit={handleSubmit} data-id="element-370" data-mp="26np36l02nv2x">
              <div className="space-y-4 px-5 py-4" data-id="element-371" data-mp="21ri7rm5ay1z8">
                {isCall && <>
                    <div data-id="element-372" data-mp="1wrvs762049n9">
                      <label htmlFor="call-outcome" className="mb-1.5 block text-[12px] font-medium text-ink" data-id="element-373" data-mp="1wt6x5976nnfy">
                        Call outcome
                      </label>
                      <select id="call-outcome" value={outcome} onChange={event => setOutcome(event.target.value)} className={inputClass} data-id="element-374" data-mp="1is49wt4gc73g">
                        {callOutcomes.map(option => <option key={option} value={option} data-id="element-375" data-mp="4ul3fwyig2o">
                            {option}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-376" data-mp="3nb3pjhb39dll">
                      <label htmlFor="call-notes" className="mb-1.5 block text-[12px] font-medium text-ink" data-id="element-377" data-mp="3n7pocce5vafq">
                        Notes
                      </label>
                      <textarea id="call-notes" value={notes} onChange={event => setNotes(event.target.value)} rows={4} placeholder="What did you discuss?" className={`${inputClass} resize-none`} data-id="element-378" data-mp="nv753p5h839m" />
                    </div>
                  </>}

                {isSchedule && <div data-id="element-379" data-mp="2zi56jwtquj81">
                    <label htmlFor="next-follow-up" className="mb-1.5 block text-[12px] font-medium text-ink" data-id="element-380" data-mp="2s0gq5orwlveq">
                      Next follow-up date &amp; time
                    </label>
                    <input id="next-follow-up" type="datetime-local" value={followUpDate} onChange={event => setFollowUpDate(event.target.value)} min={toDateTimeInputValue(new Date().toISOString())} className={inputClass} data-id="element-381" data-mp="310lvzuhg8lqs" />
                    <p className="mt-2 text-[11.5px] text-gray-500" data-id="element-382" data-mp="2i3ayoffdo2dz">
                      Schedule when {lead.name.split(' ')[0]} should next be contacted.
                    </p>
                  </div>}

                {!isCall && !isSchedule && <>
                    <div data-id="element-383" data-mp="1km3vxbstlxqc">
                      <label htmlFor="email-subject" className="mb-1.5 block text-[12px] font-medium text-ink" data-id="element-384" data-mp="1kmvffo958509">
                        Subject
                      </label>
                      <input id="email-subject" type="text" value={subject} onChange={event => setSubject(event.target.value)} className={inputClass} data-id="element-385" data-mp="1929sggftyu5z" />
                    </div>
                    <div data-id="element-386" data-mp="1n0puvv6ntyib">
                      <label htmlFor="email-body" className="mb-1.5 block text-[12px] font-medium text-ink" data-id="element-387" data-mp="1n07z0znwtmax">
                        Message
                      </label>
                      <textarea id="email-body" value={body} onChange={event => setBody(event.target.value)} rows={6} className={`${inputClass} resize-none`} data-id="element-388" data-mp="21qxfohk53tm6" />
                    </div>
                  </>}
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3" data-id="element-389" data-mp="3mb2htixcj1pb">
                <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50" data-id="element-390" data-mp="3cas7q76lyaed">
                  Cancel
                </button>
                <button type="submit" disabled={isSchedule && !followUpDate} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-391" data-mp="42lbitagcmj1">
                  {isCall ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-392" data-mp="2a8khntzzb6za" /> : isSchedule ? <CalendarClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-393" data-mp="267qyfdxhegcq" /> : <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-394" data-mp="2k7a13r7iwzhn" />}
                  {isCall ? 'Save call' : isSchedule ? 'Set follow-up' : 'Send email'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>;
}